import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  calculateMonthlyPayment, 
  calculateTotalInterest, 
  calculateTotalRepaid,
  calculateAPR,
  calculateEffectiveAPR,
  simulateMissedPayments,
  convertMonthlyToAnnualRate
} from '../../utils/calculations';
import * as Sentry from '@sentry/browser';

const MissedPaymentCalculator = ({ onCalculate }) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      principal: 1500,
      monthlyInterestRate: 3.99,
      termInMonths: 12,
      missedPayments: '6, 9',
      lateFee: 75
    }
  });

  // Watch the form values for real-time calculations in display
  const watchedValues = watch();

  const onSubmit = async (data) => {
    try {
      setIsCalculating(true);
      console.log('Calculating missed payments with:', data);
      
      // Parse form values
      const principal = parseFloat(data.principal);
      const monthlyInterestRate = parseFloat(data.monthlyInterestRate);
      const termInMonths = parseInt(data.termInMonths, 10);
      const missedPaymentsString = (data.missedPayments || '').toString().trim();
      const lateFee = parseFloat(data.lateFee || 0);
      
      // Calculate annual interest rate from monthly rate
      const annualInterestRate = convertMonthlyToAnnualRate(monthlyInterestRate);
      
      // Parse missed payments list
      const missedPayments = missedPaymentsString 
        ? missedPaymentsString.split(',').map(item => parseInt(item.trim(), 10)).filter(n => !isNaN(n))
        : [];
      
      // Validate that missed payment months are within the loan term
      const invalidMonths = missedPayments.filter(month => month < 1 || month > termInMonths);
      if (invalidMonths.length > 0) {
        throw new Error(`Invalid missed payment months: ${invalidMonths.join(', ')}. Months must be between 1 and ${termInMonths}.`);
      }
      
      // Calculate standard repayment details
      const monthlyPayment = calculateMonthlyPayment(principal, annualInterestRate, termInMonths);
      const totalInterest = calculateTotalInterest(principal, annualInterestRate, termInMonths);
      const totalRepaid = calculateTotalRepaid(principal, annualInterestRate, termInMonths);
      
      // Calculate standard APR (without missed payments)
      const apr = calculateAPR(principal, annualInterestRate, termInMonths);
      
      // Simulate missed payments and get updated schedule
      const missedPaymentResults = simulateMissedPayments(
        principal,
        annualInterestRate,
        termInMonths,
        missedPayments,
        lateFee
      );
      
      // Calculate the new total repayment amount with missed payments
      // The schedule already includes all payments over the extended term, so just sum everything
      const newTotalRepaid = missedPaymentResults.schedule.reduce(
        (sum, payment) => sum.plus(payment.payment).plus(payment.lateFee || 0),
        calculateMonthlyPayment(0, 0, 1).minus(calculateMonthlyPayment(0, 0, 1)) // This creates a Decimal zero
      );
      
      // Debug: Log the calculation details
      console.log('Effective APR calculation details:');
      console.log('Principal:', principal);
      console.log('New Total Repaid:', newTotalRepaid.toNumber());
      console.log('New Term Months:', missedPaymentResults.newTermMonths);
      
      // Calculate effective APR with missed payments and late fees
      // Use original term to show the true cost impact penalty of missed payments
      const effectiveAPR = calculateEffectiveAPR(
        principal,
        newTotalRepaid,
        termInMonths  // Use original term to show the penalty effect
      );
      
      console.log('Calculated Effective APR:', effectiveAPR.toNumber());
      
      // Prepare results
      const results = {
        calculatorType: 'missed',
        principal,
        monthlyInterestRate,
        annualInterestRate,
        apr,
        effectiveAPR,
        termInMonths,
        monthlyPayment,
        totalInterest,
        totalRepaid: newTotalRepaid,  // Use the new total with missed payments, not the original
        originalTotalRepaid: totalRepaid,  // Keep original total for comparison
        missedPayments,
        lateFee,
        missedPaymentsTotal: missedPaymentResults.missedPaymentsTotal,
        totalLateFees: missedPaymentResults.totalLateFees,
        additionalMonths: missedPaymentResults.additionalMonths,
        originalTermMonths: missedPaymentResults.originalTermMonths,
        newTermMonths: missedPaymentResults.newTermMonths,
        additionalInterest: missedPaymentResults.additionalInterest,
        extendedTermInterestOnly: missedPaymentResults.extendedTermInterestOnly,
        lateFeeInterestCost: missedPaymentResults.lateFeeInterestCost,
        newTotalRepaid,
        schedule: missedPaymentResults.schedule
      };
      
      console.log('Missed payment calculation results:', results);
      
      // Pass results to parent component
      onCalculate(results);
    } catch (error) {
      console.error('Error calculating missed payments:', error);
      
      // Check if this is a loan viability error and display it prominently
      if (error.message && error.message.includes('unviable')) {
        // Loan viability errors are business logic, not technical errors - don't send to Sentry
        alert(`⚠️ LOAN VIABILITY WARNING ⚠️\n\n${error.message}\n\nSuggestions:\n• Reduce the number of missed payments\n• Increase the monthly payment amount\n• Reduce the late fee amount\n• Consider loan restructuring options`);
      } else {
        // Only capture technical errors in Sentry
        Sentry.captureException(error, {
          tags: {
            component: 'MissedPaymentCalculator',
            calculatorType: 'missed'
          },
          extra: {
            formData: data,
            principal: data.principal,
            monthlyInterestRate: data.monthlyInterestRate,
            termInMonths: data.termInMonths,
            missedPayments: data.missedPayments,
            lateFee: data.lateFee
          }
        });
        
        alert(`Error: ${error.message || 'An error occurred during calculation. Please check your input values.'}`);
      }
    } finally {
      setIsCalculating(false);
    }
  };

  // Calculate derived rates for display
  const calculateDisplayRates = () => {
    if (!watchedValues.monthlyInterestRate || !watchedValues.principal) {
      return {
        annualRate: 0,
        aprRate: 0,
        dailyRate: 0,
        dailyAmount: 0
      };
    }

    const monthlyRate = parseFloat(watchedValues.monthlyInterestRate) || 0;
    const principal = parseFloat(watchedValues.principal) || 0;
    const termInMonths = parseInt(watchedValues.termInMonths, 10) || 1;
    const annualRate = convertMonthlyToAnnualRate(monthlyRate);
    
    // Calculate proper APR using the calculateAPR function
    const apr = calculateAPR(principal, annualRate, termInMonths);
    
    return {
      annualRate,
      aprRate: apr.toNumber(),
      dailyRate: annualRate / 365,
      dailyAmount: (principal * annualRate / 100) / 365
    };
  };

  const displayRates = calculateDisplayRates();

  return (
    <div className="card">
      <h2 className="section-title">Missed Payment Calculator</h2>
      <p className="mb-4 text-gray-600">
        Calculate the impact of missed payments on your loan. When payments are missed, interest continues to accrue and is capitalized (added to the outstanding balance), extending the loan term while keeping the same monthly payment amount.
      </p>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Principal Amount (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="0.01"
              {...register("principal", { 
                required: "Principal amount is required", 
                min: { value: 1, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.principal && (
              <p className="mt-1 text-sm text-red-600">{errors.principal.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Interest Rate (%)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("monthlyInterestRate", { 
                required: "Monthly interest rate is required",
                min: { value: 0, message: "Rate must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.monthlyInterestRate && (
              <p className="mt-1 text-sm text-red-600">{errors.monthlyInterestRate.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Term (months)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="1"
              {...register("termInMonths", { 
                required: "Loan term is required",
                min: { value: 1, message: "Term must be at least 1 month" },
                valueAsNumber: true
              })}
            />
            {errors.termInMonths && (
              <p className="mt-1 text-sm text-red-600">{errors.termInMonths.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Missed Payment Months (comma separated)
            </label>
            <input
              type="text"
              className="form-input box-border"
              placeholder="e.g. 3, 6, 9"
              {...register("missedPayments", { 
                required: "At least one missed payment month is required",
                pattern: {
                  value: /^(\s*\d+\s*,\s*)*(\s*\d+\s*)$/,
                  message: "Enter comma-separated numbers (e.g. 3, 6, 9)"
                }
              })}
            />
            {errors.missedPayments && (
              <p className="mt-1 text-sm text-red-600">{errors.missedPayments.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Enter the month numbers when payments are missed (e.g. 3, 6, 9)
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Late Fee per Missed Payment (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("lateFee", { 
                min: { value: 0, message: "Fee must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.lateFee && (
              <p className="mt-1 text-sm text-red-600">{errors.lateFee.message}</p>
            )}
          </div>
        </div>

        {/* Calculated Rates Display */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Calculated Interest Rates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600 font-medium">Annual Rate</p>
              <p className="text-lg font-semibold text-blue-700">{displayRates.annualRate.toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">APR</p>
              <p className="text-lg font-semibold text-blue-700">{displayRates.aprRate.toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Daily Rate (%)</p>
              <p className="text-lg font-semibold text-blue-700">{displayRates.dailyRate.toFixed(4)}%</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Daily Interest (£)</p>
              <p className="text-lg font-semibold text-blue-700">£{displayRates.dailyAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>How missed payments work (Capitalization of Arrears):</strong>
              </p>
              <ul className="mt-1 text-sm text-blue-700 list-disc list-inside space-y-1">
                <li><strong>Monthly payment amount stays the same</strong> - you don't pay extra per month</li>
                <li><strong>Interest continues to accrue</strong> during missed months and is <strong>capitalized (added to your loan balance)</strong></li>
                <li><strong>Late fees (if any) are also capitalized</strong> - they become part of the outstanding debt and accrue interest</li>
                <li>The capitalized interest and fees create a <strong>larger balance</strong> that requires <strong>additional months to repay</strong></li>
                <li>The actual term extension is <strong>typically longer</strong> than just the missed payment count due to <strong>compounding interest</strong></li>
                <li><strong>⚠️ Warning:</strong> If too many payments are missed, the loan may become <strong>unviable</strong> (monthly payment &lt; monthly interest)</li>
              </ul>
              <div className="mt-3 pt-3 border-t border-blue-300">
                <p className="text-sm text-blue-800 font-medium">
                  💡 This calculator shows you exactly how the missed payment amounts get "built into" (capitalized into) the remaining loan balance, including all accrued interest and late fees.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <button 
            type="submit" 
            className="btn btn-primary cursor-pointer"
            disabled={isCalculating}
          >
            {isCalculating ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MissedPaymentCalculator;